import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { Log, LogRow } from "../types";
import { logRowToLog } from "../types";
import { uuid } from "../utils/uuid";

export function useLogs(ownerId?: string) {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // MULTI-TENANT: Ne pas charger tous les logs si ownerId pas encore connu
      if (!ownerId) {
        setLogs([]);
        setLoading(false);
        return;
      }
      const { data, error: e } = await supabase
        .from("logs")
        .select("*")
        .eq("owner_id", ownerId)
        .order("timestamp", { ascending: false })
        .limit(200);
      if (e) throw e;
      const rows = (data ?? []) as LogRow[];
      setLogs(rows.map(logRowToLog));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement logs");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const add = useCallback(async (action: string, user: string, details: string) => {
    try {
      const row = {
        id: uuid(),
        action,
        user,
        timestamp: new Date().toISOString(),
        details,
        owner_id: ownerId, // MULTI-TENANT
      };
      const { data, error: e } = await supabase.from("logs").insert(row).select().single();
      if (e) throw e;
      const log = logRowToLog(data as LogRow);
      setLogs((prev) => [log, ...prev]);
      return { data: log, error: null };
    } catch (err) {
      return {
        data: null,
        error: err instanceof Error ? err.message : "Erreur ajout log",
      };
    }
  }, [ownerId]);

  const clear = useCallback(async () => {
    try {
      const ids = logs.slice(0, 500).map((l) => l.id);
      if (ids.length === 0) {
        setLogs([]);
        return { error: null };
      }
      const { error: e } = await supabase.from("logs").delete().in("id", ids);
      if (e) throw e;
      setLogs((prev) => prev.filter((l) => !ids.includes(l.id)));
      return { error: null };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : "Erreur effacement logs",
      };
    }
  }, [logs]);

  const remove = useCallback(async (id: string) => {
    try {
      const { error: e } = await supabase.from("logs").delete().eq("id", id);
      if (e) throw e;
      setLogs((prev) => prev.filter((l) => l.id !== id));
      return { error: null };
    } catch (err) {
      return {
        error: err instanceof Error ? err.message : "Erreur suppression log",
      };
    }
  }, []);

  return { logs, loading, error, refetch: fetchAll, add, clear, remove };
}
